import React, { PureComponent } from 'react'
import Icon from '@mdi/react'
import { normalizeDsaHudModality } from './DsaCineHud'
import { mdiDrag } from '@mdi/js'

const STORAGE_KEY = 'udv-linked-panels-layout-by-mod'
const accent = '#A5D6A7'
const PLAY_OPACITY = 0.3
/** Opacidad en Play (se restaura al pasar el ratón). */

function defaultLayout() {
  return {
    mode: 'free',
    x: Math.max(8, Math.round((typeof window !== 'undefined' ? window.innerWidth || 360 : 360) / 2 - 180)),
    y: Math.max(8, (typeof window !== 'undefined' ? window.innerHeight || 640 : 640) - 220),
  }
}

/**
 * Grupo acoplado: siempre free flotante.
 */
function normalizeLayout(parsed) {
  if (!parsed || typeof parsed !== 'object') return defaultLayout()
  const x = Number(parsed.x)
  const y = Number(parsed.y)
  if (!Number.isFinite(x) || !Number.isFinite(y)) return defaultLayout()
  const vw = typeof window !== 'undefined' ? window.innerWidth || 360 : 360
  const vh = typeof window !== 'undefined' ? window.innerHeight || 640 : 640
  return {
    mode: 'free',
    x: Math.round(Math.max(4, Math.min(vw - 80, x))),
    y: Math.round(Math.max(4, Math.min(vh - 80, y))),
  }
}

export function loadLinkedPanelsLayout(modality) {
  const key = normalizeDsaHudModality(modality)
  try {
    const raw = localStorage.getItem(STORAGE_KEY)
    if (!raw) return defaultLayout()
    const map = JSON.parse(raw)
    if (map && map[key]) return normalizeLayout(map[key])
    if (map && map.DEFAULT) return normalizeLayout(map.DEFAULT)
  } catch (e) {
    /* ignore */
  }
  return defaultLayout()
}

export function saveLinkedPanelsLayout(layout, modality) {
  const key = normalizeDsaHudModality(modality)
  // No persistir posición free del grupo (sesión); disco = placeholder
  const next = { mode: 'free', x: null, y: null }
  try {
    let map = {}
    const raw = localStorage.getItem(STORAGE_KEY)
    if (raw) {
      try {
        map = JSON.parse(raw) || {}
      } catch (e) {
        map = {}
      }
    }
    map[key] = next
    localStorage.setItem(STORAGE_KEY, JSON.stringify(map))
  } catch (e) {
    /* ignore */
  }
}

/**
 * Contenedor cine + DSA acoplados.
 * Free solo en memoria de sesión; al soltar fuera de zona segura o si deja
 * de verse → vuelve a bottom automáticamente.
 */
class LinkedPanelsGroup extends PureComponent {
  _drag = null
  _ensureTimer = null
  state = { hovered: false, dragEpoch: 0 }

  componentDidMount() {
    this.scheduleEnsureVisible()
  }

  componentDidUpdate(prevProps) {
    const prev = prevProps.layout || {}
    const next = this.props.layout || {}
    if (prev.mode !== next.mode || prev.x !== next.x || prev.y !== next.y) {
      this.scheduleEnsureVisible()
    }
  }

  componentWillUnmount() {
    if (this._ensureTimer) clearTimeout(this._ensureTimer)
  }

  setRootRef = (el) => {
    this._rootEl = el
  }

  scheduleEnsureVisible = () => {
    if (this._ensureTimer) clearTimeout(this._ensureTimer)
    this._ensureTimer = setTimeout(() => {
      this._ensureTimer = null
      this.ensureVisible()
    }, 80)
  }

  /** Si el grupo no se ve en viewport → forzar bottom. */
  ensureVisible = () => {
    if (this._drag) return
    const layout = this.props.layout || defaultLayout()
    if (layout.mode !== 'free') return
    const el = this._rootEl
    if (!el || typeof this.props.onLayoutChange !== 'function') return
    let r
    try {
      r = el.getBoundingClientRect()
    } catch (e) {
      return
    }
    // Medición inestable justo tras el drop: no forzar bottom (eso centraba el cine).
    if (!r || r.width < 8 || r.height < 8) return

    const vw = window.innerWidth || 360
    const vh = window.innerHeight || 640
    const clearlyOff =
      r.right < -40 ||
      r.bottom < -40 ||
      r.left > vw + 40 ||
      r.top > vh + 40
    if (clearlyOff) {
      const fb = defaultLayout()
      this.applyFreeChrome(fb.x, fb.y)
      this.props.onLayoutChange(fb)
    }
  }

  getContentOrigin = () => {
    const root = this._rootEl
    if (!root) {
      return {
        x: Math.max(8, Math.round((window.innerWidth || 360) / 2 - 200)),
        y: Math.max(8, (window.innerHeight || 640) - 220),
      }
    }
    try {
      const kids = root.children
      if (kids && kids.length) {
        let left = Infinity
        let top = Infinity
        for (let i = 0; i < kids.length; i++) {
          const r = kids[i].getBoundingClientRect()
          if (!r.width && !r.height) continue
          left = Math.min(left, r.left)
          top = Math.min(top, r.top)
        }
        if (left < Infinity) {
          return { x: Math.round(left), y: Math.round(top) }
        }
      }
    } catch (e) {
      /* ignore */
    }
    const r = root.getBoundingClientRect()
    if (r.width > (window.innerWidth || 360) * 0.85) {
      return {
        x: Math.max(8, Math.round((window.innerWidth || 360) / 2 - 200)),
        y: Math.round(r.top),
      }
    }
    return { x: Math.round(r.left), y: Math.round(r.top) }
  }

  getContentSize = () => {
    const root = this._rootEl
    if (!root) return { w: 360, h: 160 }
    try {
      const kids = root.children
      if (kids && kids.length) {
        let left = Infinity
        let top = Infinity
        let right = -Infinity
        let bottom = -Infinity
        for (let i = 0; i < kids.length; i++) {
          const r = kids[i].getBoundingClientRect()
          if (!r.width && !r.height) continue
          left = Math.min(left, r.left)
          top = Math.min(top, r.top)
          right = Math.max(right, r.right)
          bottom = Math.max(bottom, r.bottom)
        }
        if (left < Infinity) {
          return {
            w: Math.max(120, Math.round(right - left)),
            h: Math.max(80, Math.round(bottom - top)),
          }
        }
      }
    } catch (e) {
      /* ignore */
    }
    const w = root.offsetWidth || 360
    const h = root.offsetHeight || 160
    if (w > (window.innerWidth || 360) * 0.85) {
      return { w: 360, h: Math.min(h, 200) }
    }
    return { w, h }
  }

  applyFreeChrome = (x, y) => {
    if (!this._rootEl) return
    Object.assign(this._rootEl.style, {
      position: 'fixed',
      left: `${Math.round(x)}px`,
      top: `${Math.round(y)}px`,
      bottom: 'auto',
      right: 'auto',
      width: 'auto',
      maxWidth: '100vw',
      transform: 'none',
      justifyContent: 'flex-start',
      boxShadow: 'none',
      padding: '0',
      visibility: 'visible',
      display: 'flex',
    })
  }

  /**
   * Nunca vaciar style.* tras un drag: React no re-aplica props idénticas
   * y el panel quedaba sin position → “desaparecía” al soltar.
   * No tocar opacity aquí: la controla React (dim en Play).
   */
  applyBottomChrome = () => {
    if (!this._rootEl) return
    Object.assign(this._rootEl.style, {
      position: 'fixed',
      left: '0px',
      right: '0px',
      width: '100%',
      top: 'auto',
      bottom: 'var(--udv-cine-bottom, 56px)',
      maxWidth: '',
      transform: 'none',
      justifyContent: 'center',
      boxShadow: 'none',
      padding: '0px 8px 4px',
      visibility: 'visible',
      display: 'flex',
    })
  }

  /** Aplica el chrome del layout actual (props) sin dejar el nodo huérfano. */
  syncChromeFromLayout = (layout) => {
    const L = layout || this.props.layout || defaultLayout()
    const x = Number.isFinite(Number(L.x)) ? Number(L.x) : defaultLayout().x
    const y = Number.isFinite(Number(L.y)) ? Number(L.y) : defaultLayout().y
    this.applyFreeChrome(x, y)
  }

  clearInlineChrome = () => {
    // Compat: no borrar a vacío — restaurar según layout.
    this.syncChromeFromLayout(this.props.layout)
  }

  getLinkedDragHandlers = () => ({
    onPointerDown: this.onDragPointerDown,
    onPointerMove: this.onDragPointerMove,
    onPointerUp: this.onDragPointerUp,
    onPointerCancel: this.onDragPointerUp,
  })

  onDragPointerDown = (e) => {
    const layout = this.props.layout || defaultLayout()
    const fromBottom = layout.mode !== 'free'
    let originX
    let originY

    if (fromBottom) {
      const origin = this.getContentOrigin()
      originX = origin.x
      originY = origin.y
      // No pasar a free hasta que haya movimiento real (evita salto + pelea con React).
    } else {
      const rect = this._rootEl && this._rootEl.getBoundingClientRect()
      originX = Number.isFinite(Number(layout.x))
        ? Number(layout.x)
        : rect
          ? rect.left
          : e.clientX
      originY = Number.isFinite(Number(layout.y))
        ? Number(layout.y)
        : rect
          ? rect.top
          : e.clientY
    }

    this._drag = {
      startX: e.clientX,
      startY: e.clientY,
      originX,
      originY,
      fromBottom,
      moved: false,
    }
    this._pendingPos = { x: Math.round(originX), y: Math.round(originY) }
    try {
      e.currentTarget.setPointerCapture(e.pointerId)
    } catch (err) {
      /* ignore */
    }
    e.preventDefault()
    e.stopPropagation()
  }

  onDragPointerMove = (e) => {
    if (!this._drag) return
    const dx = e.clientX - this._drag.startX
    const dy = e.clientY - this._drag.startY
    if (Math.abs(dx) > 4 || Math.abs(dy) > 4) {
      if (!this._drag.moved && this._drag.fromBottom) {
        this.applyFreeChrome(this._drag.originX, this._drag.originY)
      }
      this._drag.moved = true
    }
    if (!this._drag.moved) {
      e.stopPropagation()
      return
    }
    const size = this.getContentSize()
    const vw = window.innerWidth || 360
    const vh = window.innerHeight || 640
    let cineBottom = 56
    try {
      const css = getComputedStyle(document.documentElement).getPropertyValue(
        '--udv-cine-bottom'
      )
      const n = parseFloat(css)
      if (Number.isFinite(n)) cineBottom = n
    } catch (err) {
      /* ignore */
    }
    const cw = Math.min(size.w, 420)
    const ch = Math.min(size.h, 280)
    const x = Math.max(4, Math.min(vw - cw - 4, this._drag.originX + dx))
    const maxY = Math.max(4, vh - cineBottom - ch - 8)
    const y = Math.max(4, Math.min(maxY, this._drag.originY + dy))
    this._pendingPos = { x: Math.round(x), y: Math.round(y) }
    this.applyFreeChrome(this._pendingPos.x, this._pendingPos.y)
    e.stopPropagation()
  }

  onDragPointerUp = (e) => {
    if (!this._drag) return
    const drag = this._drag
    this._drag = null

    if (!drag.moved) {
      // Click sin arrastre: restaurar chrome del layout (no vaciar estilos).
      this.syncChromeFromLayout(this.props.layout)
      this._pendingPos = null
      try {
        e.currentTarget.releasePointerCapture(e.pointerId)
      } catch (err) {
        /* ignore */
      }
      e.stopPropagation()
      return
    }

    const size = this.getContentSize()
    const pending = this._pendingPos || {
      x: drag.originX,
      y: drag.originY,
    }
    const vw = window.innerWidth || 360
    const vh = window.innerHeight || 640
    let cineBottom = 56
    try {
      const css = getComputedStyle(document.documentElement).getPropertyValue(
        '--udv-cine-bottom'
      )
      const n = parseFloat(css)
      if (Number.isFinite(n)) cineBottom = n
    } catch (err) {
      /* ignore */
    }

    const cw = Math.min(size.w, 420)
    const ch = Math.min(size.h, 280)
    const nx = Math.max(4, Math.min(vw - cw - 4, pending.x))
    // Free cerca del borde: quedar arriba de la barra, sin autodock a centro.
    const maxY = Math.max(4, vh - cineBottom - ch - 8)
    const ny = Math.max(4, Math.min(maxY, pending.y))
    const drop = normalizeLayout({ mode: 'free', x: nx, y: ny })

    // Aplicar destino YA (antes del setState). Nunca clear→vacío.
    this.syncChromeFromLayout(drop)
    this._pendingPos = null

    if (typeof this.props.onLayoutChange === 'function') {
      this.props.onLayoutChange(drop)
    }
    // Forzar que React re-escriba style aunque el modo no cambie (bottom→bottom).
    this.setState({ dragEpoch: (this.state.dragEpoch || 0) + 1 })
    this.scheduleEnsureVisible()

    try {
      e.currentTarget.releasePointerCapture(e.pointerId)
    } catch (err) {
      /* ignore */
    }
    e.stopPropagation()
  }

  dockBottom = () => {
    // Aparcar el grupo en posición free inferior (ya no hay modo bottom)
    const drop = defaultLayout()
    if (typeof this.props.onLayoutChange === 'function') {
      this.props.onLayoutChange(drop)
    }
  }

  render() {
    const { layout = defaultLayout(), inPlay = false, stopPointer } = this.props
    const mode = 'free'
    const dimmed = !!inPlay && !this.state.hovered && !this._drag
    const stop = stopPointer || ((e) => e.stopPropagation())

    const style = {
      display: 'flex',
      flexDirection: 'row',
      alignItems: 'flex-end',
      justifyContent: 'flex-start',
      gap: 8,
      pointerEvents: 'none',
      zIndex: 140000,
      opacity: dimmed ? PLAY_OPACITY : 1,
      transition: 'opacity 0.2s ease',
      padding: 0,
      boxSizing: 'border-box',
      visibility: 'visible',
      ['--udv-lp-epoch']: String(this.state.dragEpoch || 0),
      position: 'fixed',
      left: Number.isFinite(Number(layout.x)) ? Number(layout.x) : 8,
      top: Number.isFinite(Number(layout.y))
        ? Number(layout.y)
        : Math.max(8, (window.innerHeight || 640) - 220),
      bottom: 'auto',
      right: 'auto',
      width: 'auto',
      maxWidth: '100vw',
    }

    const dragHandlers = this.getLinkedDragHandlers()
    const wrap = (node) => {
      if (!React.isValidElement(node)) return null
      return React.cloneElement(node, {
        embedded: true,
        linkedDragHandlers: dragHandlers,
        onRelinkBottom: this.dockBottom,
        onLinkedGroupMode: this.props.onLayoutChange,
        onUncouple: this.props.onUncouple,
      })
    }
    const dsaNode = wrap(this.props.dsaPanel)
    const cineNode = wrap(this.props.cinePanel)

    return (
      <div
        ref={this.setRootRef}
        data-linked-panels="true"
        data-linked-mode={mode}
        onPointerDown={stop}
        onMouseEnter={() => this.setState({ hovered: true })}
        onMouseLeave={() => this.setState({ hovered: false })}
        onPointerEnter={() => this.setState({ hovered: true })}
        onPointerLeave={() => this.setState({ hovered: false })}
        style={style}
      >
        <div
          title="Mover cine + DSA acoplados · desacoplar con ✕ en cualquiera"
          onPointerDown={this.onDragPointerDown}
          onPointerMove={this.onDragPointerMove}
          onPointerUp={this.onDragPointerUp}
          onPointerCancel={this.onDragPointerUp}
          style={{
            pointerEvents: 'auto',
            alignSelf: 'flex-end',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: 22,
            minHeight: 64,
            marginBottom: 4,
            borderRadius: 6,
            background: 'rgba(40,40,48,0.85)',
            border: '1px solid rgba(255,255,255,0.1)',
            cursor: 'grab',
            touchAction: 'none',
            flexShrink: 0,
          }}
        >
          <Icon path={mdiDrag} size="1rem" color={accent} />
        </div>
        {this.props.dsaSide === 'right' ? (
          <>
            {cineNode}
            {dsaNode}
          </>
        ) : (
          <>
            {dsaNode}
            {cineNode}
          </>
        )}
      </div>
    )
  }
}

export default LinkedPanelsGroup
